import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { PrimeNGSharedModule } from "../../../../../shared/prime/prime-ng.module";
import { MessageService } from 'primeng/api';
import { ManageAssessmentApiService } from "../../../../../core/services/api/manage-assessment-api.service";
import { AuditPermissionService } from "../../../../../core/services/audit-permission.service";
import { HttpErrorResponse } from '@angular/common/http';

interface GlobalRaUser {
  name: string;
  email: string;
  id: number; // User ID for filtering
}

@Component({
  selector: 'app-assessment-share-popup',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    PrimeNGSharedModule
  ],
  templateUrl: "./assessment-share-popup.component.html",
  styleUrls: ["./assessment-share-popup.component.scss"],
  providers: [MessageService]
})
export class AssessmentSharePopupComponent implements OnInit, OnDestroy, OnChanges {
  @Input() visible: boolean = false;
  @Output() visibleChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() closeDialog = new EventEmitter<void>();
  @Input() config: any | null = null;
  @Input() assessmentData: any | null = null; // Add input for assessment data
  @Output() shareConfirmed = new EventEmitter<{
    globalRa: GlobalRaUser;
    emailId: string;
    remarks: string;
  }>();

  shareForm!: FormGroup;
  private unsubscribe$ = new Subject<void>();

  allGlobalRaUsers: GlobalRaUser[] = []; // Store all fetched users
  filteredGlobalRaUsers: GlobalRaUser[] = []; // Store filtered users (excluding already shared)

  constructor(
    private fb: FormBuilder,
    private messageService: MessageService,
    private manageAssessmentApiService: ManageAssessmentApiService,
    private auditService: AuditPermissionService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeForm();
    this.fetchGlobalRaUsers();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-fetch users when assessment config changes or popup becomes visible
    if ((changes['config'] && this.config?.assessmentId) || 
        (changes['visible'] && this.visible && this.config?.assessmentId)) {
      this.fetchGlobalRaUsers();
    } else if ((changes['assessmentData'] || changes['visible']) && this.visible) {
      // Fallback to client-side filtering if using old method
      this.filterAvailableUsers();
    }
  }

  initializeForm(): void {
    this.shareForm = this.fb.group({
      globalRa: [null, Validators.required],
      emailId: ['', [Validators.required, Validators.email]],
      remark: ['']
    });
  }

  fetchGlobalRaUsers(): void {
    // If we have assessment data with RCA ID, use the filtered API
    if (this.config?.assessmentId) {
      this.auditService.getGlobalUserNameExcludingRca(this.config.assessmentId).subscribe({
        next: (response: any) => {
          this.allGlobalRaUsers = response?.users || [];
          this.filteredGlobalRaUsers = [...this.allGlobalRaUsers]; // No need for client-side filtering
        },
        error: (error) => {
          console.error("Error loading filtered user list:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to load user list. Please try again.",
            life: 5000,
          });
        },
      });
    } else {
      // Fallback to original method if no assessment ID
      this.auditService.getGlobalUserName().subscribe({
        next: (response: any) => {
          this.allGlobalRaUsers = response?.users || [];
          this.filterAvailableUsers();
        },
        error: (error) => {
          console.error("Error loading user list:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to load user list. Please try again.",
            life: 5000,
          });
        },
      });
    }
  }

  /**
   * Filters out users who are already shared with the current assessment
   * This method is used as a fallback when server-side filtering is not available
   */
  filterAvailableUsers(): void {
    if (!this.assessmentData?.shared_user_ids) {
      this.filteredGlobalRaUsers = [...this.allGlobalRaUsers];
      return;
    }

    // Filter out users who are already shared (client-side fallback)
    this.filteredGlobalRaUsers = this.allGlobalRaUsers.filter(user => 
      !this.assessmentData.shared_user_ids.includes(user.id)
    );
    this.cdr.detectChanges();
  }

  onGlobalRaChange(event: any): void {
    const selectedUser: GlobalRaUser = event.value;
    if (selectedUser) {
      this.shareForm.get('emailId')?.patchValue(selectedUser.email);
    } else {
      this.shareForm.get('emailId')?.patchValue('');
    }
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.closeDialog.emit();
    this.shareForm.reset(); // Reset the form when closing
    this.shareForm.get('emailId')?.disable(); // Ensure email field is disabled after reset
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onSubmit(): void {
    if (this.shareForm.valid) {
      const formValue = this.shareForm.value;
      const apiPayload = {
        user_ids: [formValue?.globalRa?.id]
      }
      this.manageAssessmentApiService
      .shareRca(apiPayload, this.config?.assessmentId)
      .subscribe({
        next: (response: any) => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `RCA shared successfully!`,
            life: 5000,
          });
          
          // Refresh the user list after successful sharing
          this.fetchGlobalRaUsers();
          
          // Only emit shareConfirmed if the API call was successful
          this.shareConfirmed.emit({
            globalRa: formValue.globalRa,
            emailId: formValue.emailId,
            remarks: formValue.remark
          });
          this.close();
        },
        error: (error: HttpErrorResponse) => {
          console.error("Failed to share RCA:", error);
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail:
              error.error?.message ||
              "Failed to share RCA. Please try again.",
            life: 5000,
          });
          // Don't close the dialog on error so user can try again
        },
      });
    } else {
      this.shareForm.markAllAsTouched();
      this.messageService.add({
        severity: "warn",
        summary: "Validation",
        detail: "Please fill in all required fields.",
        life: 3000,
      });
    }
  }

  onGlobalRaFilter(event: any): void {
    const query = event.filter.toLowerCase();
    this.auditService.getGlobalUserName(1, 10, query).pipe(takeUntil(this.unsubscribe$)).subscribe({
      next: (response: any) => {
        this.allGlobalRaUsers = response?.users || [];
        this.filterAvailableUsers();
      },
      error: (error) => {
        console.error("Error loading user list:", error);
        this.messageService.add({
          severity: "error",
          summary: "Error",
          detail:
            error.error?.message ||
            "Failed to load user list. Please try again.",
          life: 5000,
        });
      },
    });
  }
  debouncedSearch = this.debounce(this.onGlobalRaFilter.bind(this), 500);

  debounce(func: any, delay: number) {
    let timeoutId: any;

    return (...args: any) => { 
      const context = this; 
      clearTimeout(timeoutId);

      timeoutId = setTimeout(() => {
        func.apply(context, args);
      }, delay);
    };
  }
}